import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CultureLab = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('culture-lab');
    if (element) observer?.observe(element);

    return () => observer?.disconnect();
  }, []);

  const cultureTabs = [
    {
      id: "workspace",
      title: "Our Workspace",
      icon: "Building",
      content: {
        title: "Where Innovation Happens",
        description: "Our office is designed to inspire creativity and foster collaboration. From open collaboration spaces to quiet focus zones, every corner is crafted to support different work styles and spark innovation.",
        highlights: [
          "Open collaboration areas with 3D displays",
          "Quiet focus pods for deep work",
          "Gaming lounge for team bonding",
          "Rooftop garden for inspiration breaks"
        ]
      }
    },
    {
      id: "process",
      title: "Our Process",
      icon: "Cog",
      content: {
        title: "How We Build Magic",
        description: "Our development process combines agile methodologies with creative exploration. We believe in rapid prototyping, continuous feedback, and iterative improvement to deliver exceptional results.",
        highlights: [
          "Design thinking workshops",
          "Rapid prototyping sessions",
          "Weekly demo days",
          "Continuous integration & deployment"
        ]
      }
    },
    {
      id: "collaboration",
      title: "Collaboration",
      icon: "Users",
      content: {
        title: "Teamwork Makes the Dream Work",
        description: "We foster a culture of open communication, shared ownership, and collective problem-solving. Every voice matters, and every idea has the potential to transform our work.",
        highlights: [
          "Daily stand-ups and retrospectives",
          "Cross-functional project teams",
          "Mentorship programs",
          "Knowledge sharing sessions"
        ]
      }
    },
    {
      id: "growth",
      title: "Growth & Learning",
      icon: "TrendingUp",
      content: {
        title: "Never Stop Growing",
        description: "We invest heavily in our team's professional development. From conference attendance to internal workshops, we ensure everyone has opportunities to learn and grow.",
        highlights: [
          "Annual learning budget for each team member",
          "Internal tech talks and workshops",
          "Conference attendance and speaking",
          "Open source contribution time"
        ]
      }
    }
  ];

  const galleryImages = [
    {
      src: "https://lh3.googleusercontent.com/p/AF1QipPRtctHCSoeiy9x-8QUuNUxM7UHvmdauVKDCcB3=s1360-w1360-h1020-rw",
      alt: "Modern office workspace with collaborative areas",
      caption: "Our main collaboration space"
    },
    {
      src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop",
      alt: "Team brainstorming session",
      caption: "Weekly brainstorming sessions"
    },
    {
      src: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&h=400&fit=crop",
      alt: "Developer working on code",
      caption: "Focused development time"
    },
    {
      src: "https://lh3.googleusercontent.com/p/AF1QipOo3L_8yayOl4MbslYs-iUrzhXKQpEXZmIPdWC1=s1360-w1360-h1020-rw",
      alt: "Team meeting in conference room",
      caption: "Client presentation setup"
    },
    {
      src: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&h=400&fit=crop",
      alt: "Team celebration",
      caption: "Celebrating project milestones"
    },
    {
      src: "https://lh3.googleusercontent.com/p/AF1QipOfneOdZJoQXCRkuDjaABdtZQNsIUBgx0Acn6hl=s1360-w1360-h1020-rw",
      alt: "Refuel station with coffee and snacks",
      caption: "caffeine station for creative boosts"
    },
    {
      src: "https://lh3.googleusercontent.com/p/AF1QipPIlA6nylCo_vKGUAM039mUcA9PbdV1C4oTjwhg=s1360-w1360-h1020-rw",
      alt: "Workshop session setup",
      caption: "Workshop session setup"
    },
    {
      src: "https://lh3.googleusercontent.com/p/AF1QipPajwUBHuuXvAU-IwiNoRUB-jr9wsGs8YuZU3le=s1360-w1360-h1020-rw",
      alt: "Area for office gossip and relaxation",
      caption: "Area for office gossip and relaxation"
    },
    {
      src: "https://lh3.googleusercontent.com/p/AF1QipMN_rDCOR13jdhE8HDELOR1Y4vWxYXjD5LSiU7q=s1360-w1360-h1020-rw",
      alt: "A small peek into our office culture",
      caption: "A small peek into our office culturen"
    }
  ];

  const perks = [
    {
      title: "Flexible Work",
      description: "Remote-first culture with flexible hours",
      icon: "Clock"
    },
    {
      title: "Health & Wellness",
      description: "Comprehensive health coverage and wellness programs",
      icon: "Heart"
    },
    {
      title: "Learning Budget",
      description: "$3,000 annual budget for courses and conferences",
      icon: "BookOpen"
    },
    {
      title: "Latest Tech",
      description: "Top-tier equipment and software licenses",
      icon: "Laptop"
    },
    {
      title: "Team Events",
      description: "Regular team building and social activities",
      icon: "Calendar"
    }
  ];

  return (
    <section id="culture-lab" className="py-12 md:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Beaker" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">Behind the Scenes</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Culture Lab
          </h2>
          <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Step inside our world and discover the environment, processes, and culture that fuel our creativity and drive our success.
          </p>
        </div>

        {/* Culture Tabs */}
        <div className="mb-8 md:mb-12">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8 md:mb-12">
            {cultureTabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(index)}
                className={`flex items-center space-x-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium text-sm md:text-base transition-all ${
                  activeTab === index
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={18} />
                <span>{tab.title}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-card border border-border rounded-xl p-6 md:p-8">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3 md:mb-4">
                  {cultureTabs[activeTab].content.title}
                </h3>
                <p className="text-muted-foreground text-sm md:text-base mb-4 md:mb-6 leading-relaxed">
                  {cultureTabs[activeTab].content.description}
                </p>
                <ul className="space-y-2 md:space-y-3">
                  {cultureTabs[activeTab].content.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-accent rounded-full flex-shrink-0"></div>
                      <span className="text-foreground text-sm md:text-base">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="bg-gradient-to-br from-accent/20 to-accent/5 rounded-xl p-8 md:p-12">
                  <Icon
                    name={cultureTabs[activeTab].icon}
                    size={80}
                    className="text-accent/60 mx-auto md:w-32 md:h-32"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Office Gallery - SIMPLIFIED */}
        <div className="mb-8 md:mb-12">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-6 md:mb-8">
            A Glimpse Inside Our World
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative group overflow-hidden rounded-xl"
              >
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white font-medium text-sm">{image.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Perks & Benefits */}
        <div className="mb-8 md:mb-12">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-6 md:mb-8">
            Why You'll Love Working Here
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {perks.map((perk, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-xl mb-4">
                  <Icon name={perk.icon} size={24} className="text-accent" />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">{perk.title}</h4>
                <p className="text-muted-foreground text-sm">{perk.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Culture Quote */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-accent/10 to-success/10 border border-accent/20 rounded-xl p-6 md:p-12">
            <Icon name="Quote" size={40} className="text-accent mx-auto mb-4 md:mb-6" />
            <blockquote className="text-lg md:text-2xl font-medium text-foreground mb-4 md:mb-6 italic">
              "We don't just build software—we craft experiences that inspire, engage, and transform. Our culture is the foundation that makes this possible."
            </blockquote>
            <cite className="text-accent font-semibold text-sm md:text-base">—Founder & CEO</cite>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CultureLab;